import React, { useCallback, useState } from 'react';
import { Button, Card, Typography, Empty, List, Space, Tag, Popconfirm } from 'antd';
import { UserAddOutlined, EditOutlined, DeleteOutlined, CrownOutlined } from '@ant-design/icons';
import { useOnboardingStore } from '../stores/onboarding-store';
import SingleFieldGroupForm from './SingleFieldGroupForm';
import GroupMemberModal from './GroupMemberModal';
import ProductSelectionForm from './ProductSelectionForm';
import LocationSelectionForm from './LocationSelectionForm';
import DocumentUploadForm from './DocumentUploadForm';
import GroupReviewForm from './GroupReviewForm';
import { GroupMemberData } from '../types';
import {GroupedFieldConfigurationDTO, CustomerCategoryProductDTO, CustomerCategoryLocationDTO, CustomerCategoryDocumentDTO} from "@/shared/api/model";

const CUSTINFO_FIELDS = new Set(['ctitle', 'marital', 'housing', 'educatio', 'language', 'income', 'officer', 'kin1', 'kin2', 'relationshipManager']);

const { Title, Paragraph } = Typography;

interface GroupOnboardingFormProps {
  categoryId: number;
  currentStep: number;
  fieldConfiguration?: GroupedFieldConfigurationDTO;
  memberTemplateId?: number | null;
  categoryProducts?: CustomerCategoryProductDTO[];
  categoryLocations?: CustomerCategoryLocationDTO[];
  categoryDocuments?: CustomerCategoryDocumentDTO[];
}

const GroupOnboardingForm: React.FC<GroupOnboardingFormProps> = ({
  categoryId,
  currentStep,
  memberTemplateId,
  categoryProducts = [],
  categoryLocations = [],
  categoryDocuments = [],
}) => {
  const [memberModalVisible, setMemberModalVisible] = useState(false);
  const [editingMember, setEditingMember] = useState<GroupMemberData | null>(null);
  const [editingMemberIndex, setEditingMemberIndex] = useState<number | undefined>(undefined);

  const {
    formData,
    steps,
    updateFieldValue,
    updateFormData,
    addGroupMember,
    updateGroupMember,
    removeGroupMember,
    setGroupLeader,
    errors,
  } = useOnboardingStore();

  const handleFieldChange = useCallback((fieldName: string, value: any) => {
    updateFieldValue(CUSTINFO_FIELDS.has(fieldName) ? 'custInfoData' : 'customerData', fieldName, value);
  }, [updateFieldValue]);

  const combinedFormData = {
    ...formData.customerData,
    ...formData.custInfoData,
  };

  const handleProductsChange = useCallback((products: any[]) => {
    updateFormData('productData', products);
  }, [updateFormData]);

  const handleLocationsChange = useCallback((locations: any[]) => {
    updateFormData('locationData', locations);
  }, [updateFormData]);

  const handleDocumentsChange = useCallback((documents: any[]) => {
    updateFormData('documentData', documents);
  }, [updateFormData]);

  const handleAddMember = () => {
    setEditingMember(null);
    setEditingMemberIndex(undefined);
    setMemberModalVisible(true);
  };

  const handleEditMember = (member: GroupMemberData, index: number) => {
    setEditingMember(member);
    setEditingMemberIndex(index);
    setMemberModalVisible(true);
  };

  const handleSaveMember = (member: GroupMemberData, index?: number) => {
    if (index !== undefined) {
      // Update existing member
      updateGroupMember(index, member);
    } else {
      // Add new member - first member is leader by default
      const isFirstMember = (formData.groupMembers?.length || 0) === 0;
      addGroupMember({ ...member, isLeader: isFirstMember });
    }
    setMemberModalVisible(false);
    setEditingMember(null);
    setEditingMemberIndex(undefined);
  };

  const handleSetLeader = (memberIndex: number) => {
    setGroupLeader(memberIndex);
  };

  const handleRemoveMember = (memberIndex: number) => {
    removeGroupMember(memberIndex);
  };

  const renderGroupMemberManagement = () => {
    const members = formData.groupMembers || [];

    if (!memberTemplateId) {
      return (
        <Card>
          <Empty
            description="No member template configured for this group category"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </Card>
      );
    }

    const getMemberDisplayName = (member: GroupMemberData): string => {
      const data = member.data || {};
      return data.firstName && data.lastName 
        ? `${data.firstName} ${data.lastName}`
        : data.name || data.fullName || `Member ${members.indexOf(member) + 1}`;
    };

    return (
      <div className="group-member-management">
        <div className="member-management-header" style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <Title level={4}>Group Members</Title>
              <Paragraph>
                Add and manage group members. At least one member must be designated as the group leader.
              </Paragraph>
            </div>
            <Button
              type="primary"
              icon={<UserAddOutlined />}
              onClick={handleAddMember}
              size="large"
            >
              Add Member
            </Button>
          </div>
        </div>

        {members.length > 0 ? (
          <List
            bordered
            dataSource={members}
            renderItem={(member, index) => (
              <List.Item
                actions={[
                  !member.isLeader && (
                    <Button
                      type="link"
                      icon={<CrownOutlined />}
                      onClick={() => handleSetLeader(index)}
                      title="Set as leader"
                    >
                      Set Leader
                    </Button>
                  ),
                  <Button
                    type="link"
                    icon={<EditOutlined />}
                    onClick={() => handleEditMember(member, index)}
                  >
                    Edit
                  </Button>,
                  <Popconfirm
                    title="Remove this member?"
                    description="Are you sure you want to remove this member from the group?"
                    onConfirm={() => handleRemoveMember(index)}
                    okText="Yes"
                    cancelText="No"
                    disabled={member.isLeader && members.length > 1}
                  >
                    <Button
                      type="link"
                      danger
                      icon={<DeleteOutlined />}
                      disabled={member.isLeader && members.length > 1}
                      title={member.isLeader && members.length > 1 ? "Cannot remove the group leader" : "Remove member"}
                    >
                      Remove
                    </Button>
                  </Popconfirm>,
                ].filter(Boolean)}
              >
                <List.Item.Meta
                  title={
                    <Space>
                      <span>{getMemberDisplayName(member)}</span>
                      {member.isLeader && (
                        <Tag icon={<CrownOutlined />} color="gold">
                          Leader
                        </Tag>
                      )}
                    </Space>
                  }
                  description={
                    <Space direction="vertical" size="small">
                      {member.data?.email && <span>Email: {member.data.email}</span>}
                      {member.data?.phone && <span>Phone: {member.data.phone}</span>}
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        ) : (
          <Card>
            <Empty
              description="No members added yet"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            >
              <Button
                type="primary"
                icon={<UserAddOutlined />}
                onClick={handleAddMember}
              >
                Add First Member
              </Button>
            </Empty>
          </Card>
        )}

        {/* Member Modal */}
        <GroupMemberModal
          visible={memberModalVisible}
          memberTemplateId={memberTemplateId}
          editingMember={editingMember}
          editingIndex={editingMemberIndex}
          onSave={handleSaveMember}
          onCancel={() => {
            setMemberModalVisible(false);
            setEditingMember(null);
            setEditingMemberIndex(undefined);
          }}
        />
      </div>
    );
  };

  const renderStepContent = () => {
    console.log('GroupOnboardingForm: Rendering step', {
      currentStep,
      totalSteps: steps.length,
      memberTemplateId,
      stepIds: steps.map(s => s.id),
      currentStepData: steps[currentStep]
    });
    
    const currentStepData = steps[currentStep];
    
    if (!currentStepData) {
      console.error('GroupOnboardingForm: Invalid step - currentStepData is undefined', {
        currentStep,
        stepsLength: steps.length,
        steps: steps.map((s, i) => ({ index: i, id: s.id, component: s.component }))
      });
      
      return (
        <Card>
          <Empty
            description={
              <div>
                <p>Invalid step configuration</p>
                <p style={{ fontSize: '12px', color: '#999' }}>
                  Current step: {currentStep}, Total steps: {steps.length}
                </p>
                <p style={{ fontSize: '12px', color: '#999' }}>
                  Member Template ID: {memberTemplateId || 'Not configured'}
                </p>
              </div>
            }
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </Card>
      );
    }

    // Handle field group steps (dynamic group fields)
    if (currentStepData.component === 'FieldGroup' && currentStepData.fieldGroup) {
      return (
        <SingleFieldGroupForm
          fieldGroup={currentStepData.fieldGroup}
          formData={combinedFormData}
          onFieldChange={handleFieldChange}
          errors={errors}
        />
      );
    }

    // Handle other step types
    switch (currentStepData.component) {
      case 'GroupMemberManagement':
        return renderGroupMemberManagement();

      case 'ProductSelectionForm':
        return (
          <ProductSelectionForm
            categoryProducts={categoryProducts}
            selectedProducts={formData.productData}
            onProductsChange={handleProductsChange}
            errors={errors}
          />
        );

      case 'LocationForm':
        return (
          <LocationSelectionForm
            categoryLocations={categoryLocations}
            selectedLocations={formData.locationData}
            onLocationsChange={handleLocationsChange}
            errors={errors}
          />
        );

      case 'DocumentUploadForm':
        return (
          <DocumentUploadForm
            categoryDocuments={categoryDocuments}
            selectedDocuments={formData.documentData}
            onDocumentsChange={handleDocumentsChange}
            errors={errors}
          />
        );

      case 'ReviewForm':
        return (
          <GroupReviewForm
            categoryId={categoryId}
            categoryProducts={categoryProducts}
            categoryLocations={categoryLocations}
            categoryDocuments={categoryDocuments}
          />
        );

      default:
        return <div>Unknown component: {currentStepData.component}</div>;
    }
  };

  return (
    <div className="group-onboarding-form">
      {renderStepContent()}
    </div>
  );
};

export default GroupOnboardingForm;
